import { rewriteEntityTerms } from '@/lib/terminology/entity-terms'

import {
  getErrorMessage,
  getErrorStatus,
  isCommittedTaskWriteError,
  isLostResponseError,
  NETWORK_ERROR_MESSAGE,
} from './error-utils'

/*
 * Error toast wording — one pattern for the whole app.
 *
 * Write the fallback as a calm, sentence-case statement of what didn't happen:
 *
 *   "Couldn't delete the invoice."   "Couldn't load tasks."
 *
 * Start with "Couldn't" (straight apostrophe), name the thing ("the invoice",
 * "your changes", a record's name), end with a period. No "Failed to", no
 * "Error:", no "Please", no "!", and no "Try again." — retrying is implied.
 * American spelling; Benny is "Benny", never "AI".
 *
 * Inside a catch block or onError, pass the error so the API's reason reaches
 * the person: getMutationErrorMessage for writes, getRequestErrorMessage for
 * reads (load, export, download, search…).
 *
 *   toast.error(getMutationErrorMessage(error, "Couldn't delete the invoice."))
 *   → "Couldn't delete the invoice. Paid invoices can't be deleted."
 *
 * The reason is appended only when it is meant for a person: a 4xx message
 * (validation, conflict, permission, not found), the connection message, or a
 * plain Error the client threw with words in it ("Name is required"). A 5xx,
 * a framework default ("Bad Request") or a programming error (TypeError and
 * friends) shows the fallback alone. A write whose response was lost may have
 * committed, so it shows UNCONFIRMED_MUTATION_MESSAGE instead of saying it
 * failed; a write the API says committed shows only the API's explanation.
 *
 * Toasts with no error in hand (a precondition check, a `result.success`
 * false) use the same sentence as a plain string.
 */

export const UNCONFIRMED_MUTATION_MESSAGE =
  'This change may have been saved. Refresh and review the current record before trying again.'

/** What a toast says when the call site has no sentence of its own. */
export const GENERIC_ERROR_MESSAGE = 'Something went wrong. Try again.'

/** Framework defaults that restate the status instead of giving a reason. */
const STATUS_ONLY_REASONS = new Set([
  'an error occurred',
  'bad request',
  'conflict',
  'forbidden',
  'forbidden resource',
  'not found',
  'request failed',
  'request timeout',
  'too many requests',
  'unauthorized',
  'unprocessable entity',
])

/** A server message that only repeats that the action failed. */
const RESTATED_FAILURE =
  /^(failed to|could not|couldn't|couldn’t|unable to)\b[^:.]*\.?$/i

/** "Name is required", not "lost" or "TASK_LOCKED". */
const CLIENT_MESSAGE = /^[A-Z][^\s]*\s+\S/

function endSentence(text: string): string {
  const trimmed = text.trim()
  if (!trimmed) return trimmed
  const capitalized = trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
  return /[.!?]$/.test(capitalized) ? capitalized : `${capitalized}.`
}

function hasResponseData(error: unknown): boolean {
  if (!error || typeof error !== 'object' || error instanceof Error)
    return false
  const source = error as { data?: unknown; response?: { data?: unknown } }
  return Boolean(source.data ?? source.response?.data)
}

function readUserFacingReason(error: unknown): string | undefined {
  if (!error) return undefined
  const status = getErrorStatus(error)
  let reason: string | undefined
  if (status !== undefined) {
    if (status < 400 || status >= 500) return undefined
    reason = getErrorMessage(error, '')
  } else if (hasResponseData(error)) {
    // An older axios-shaped rejection: the server answered, status unknown.
    reason = getErrorMessage(error, '')
  } else if (error instanceof TypeError) {
    // Only the transport's own wording; any other TypeError is a bug.
    reason =
      error.message === NETWORK_ERROR_MESSAGE ? NETWORK_ERROR_MESSAGE : ''
  } else if (error instanceof Error && error.name === 'Error') {
    // A message the client threw for a person ("Name is required").
    reason = CLIENT_MESSAGE.test(error.message.trim()) ? error.message : ''
  }
  const trimmed = reason?.trim()
  if (!trimmed) return undefined
  const normalized = trimmed.toLowerCase().replace(/\.$/, '')
  if (
    STATUS_ONLY_REASONS.has(normalized) ||
    RESTATED_FAILURE.test(trimmed) ||
    trimmed.startsWith('ThrottlerException')
  )
    return undefined
  return trimmed
}

function composeErrorMessage(error: unknown, fallback?: string): string {
  const reason = readUserFacingReason(error)
  if (!fallback) return endSentence(reason ?? GENERIC_ERROR_MESSAGE)
  const sentence = endSentence(fallback)
  if (!reason) return sentence
  const reasonSentence = endSentence(reason)
  const isRepeated = sentence
    .toLowerCase()
    .includes(reasonSentence.toLowerCase().replace(/[.!?]$/, ''))
  return isRepeated ? sentence : `${sentence} ${reasonSentence}`
}

/**
 * Toast text for a failed write. An unknown write outcome takes precedence
 * over the "couldn't" sentence. See the wording rules above.
 */
export function getMutationErrorMessage(
  error: unknown,
  fallback?: string,
): string {
  if (isLostResponseError(error)) return UNCONFIRMED_MUTATION_MESSAGE
  // Not a failure: the change saved and the API says what to do next.
  if (isCommittedTaskWriteError(error)) return getErrorMessage(error, fallback)
  return rewriteEntityTerms(composeErrorMessage(error, fallback))
}

/**
 * Failures whose feedback already has an owner: a TanStack mutation rejected
 * with them (its own onError, the MutationCache toast, or deliberate silence),
 * or the code that made the write toasted them before rethrowing. A caller
 * further up that catches the same error (UniversalField) must not toast it
 * a second time.
 */
const reportedErrors = new WeakSet<object>()

/** Record that this failure was reported (or deliberately silenced). */
export function markErrorReported(error: unknown): void {
  if (error && typeof error === 'object') reportedErrors.add(error)
}

/** The failure was already reported (or deliberately silenced) by its owner. */
export function isErrorReported(error: unknown): boolean {
  return !!error && typeof error === 'object' && reportedErrors.has(error)
}

/**
 * Toast text for a failed read (load, search, export, download). A lost
 * response leaves nothing behind, so it never says "may have been saved".
 */
export function getRequestErrorMessage(
  error: unknown,
  fallback?: string,
): string {
  return rewriteEntityTerms(composeErrorMessage(error, fallback))
}
